import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { estimateClaim, getSvTrackRouter } from '@api';
import {
  RespCode, ErrorCode, SessionStorageKey, InputStatus, env,
} from '@constants';
import {
  useQuery, useEffectOnce, useBehaviorData,
} from '@hooks';
import { logError } from '@log';
import {
  setCustomerInfo, setPolicyInfo, setClaimInfo,
} from '@store';

// 首页手机号验证正则
export const regPhoneLogin = /^\+?(\d{1,3})?\d{9,12}$/;
// eslint-disable-next-line no-control-regex
const regEmail = /(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a -z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}( ?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-z0 ]-9]：(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+ )\])/;
// const regOrderId = /^([0-9])+/;

/** 设置 email */
export const onChangeEmail = ({
  event,
  setEmail,
  emailStatus,
  setEmailStatus,
}) => {
  const { value } = event.target;
  setEmail(value.trim());
  if (emailStatus !== InputStatus.Changed) {
    setEmailStatus(InputStatus.Changed);
  }
};

/** 设置 order id */
const onChangeOrderId = ({
  event,
  setOrderId,
  orderStatus,
  setOrderStatus,
}) => {
  const { value } = event.target;
  setOrderId(value.trim());
  if (orderStatus !== InputStatus.Changed) {
    setOrderStatus(InputStatus.Changed);
  }
};

// 样式参考common的loading组件
const injectRedirectLoading = () => {
  document.body.innerHTML = `
    <div style="
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    ">
      <div style="
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: conic-gradient(from 180deg at 50% 50%, #645aff 0deg, rgba(100, 90, 255, 0) 360deg);
        display: flex;
        justify-content: center;
        align-items: center;
        animation: spinner 1s linear infinite;
      ">
        <div style="
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: #fff;
        "></div>
      </div>
    </div>

    <style>
      @keyframes spinner {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      @media (max-width: 800px) {
        div[style*="background: conic-gradient"] {
          width: 50px !important;
          height: 50px !important;
        }

        div[style*="background: #fff"] {
          width: 40px !important;
          height: 40px !important;
        }
      }
    </style>
  `;
};
/** 提交信息 */
const submit = async ({
  email,
  orderId,
  setIsInvalidBarShow,
  setIsLoading,
  navigate,
  dispatch,
  setReportData,
}) => {
  try {
    setIsLoading(true);
    setReportData({ loginTime: Date.now() });
    dispatch(setPolicyInfo({ email, orderNumber: orderId }));
    // 优先判断17保单号是否需要进4.0查还是2.0查
    let newRouter = false;
    const svTrackRes = await getSvTrackRouter(email, orderId);
    const { code: svTrackRouterCode, data: svTrackRouterData } = svTrackRes;
    if (svTrackRouterCode === 0) {
      newRouter = svTrackRouterData.router === 'new';
    }
    if (newRouter) {
      const { search } = window.location || {};
      // 解析现有的 search 参数（例如 '?email=xxx'）
      const params = new URLSearchParams(search);
      // 将 URLSearchParams 转换为普通对象
      const queryParams = Object.fromEntries(params.entries());
      // 添加 email 和 order_number 参数
      params.set('email', email);
      params.set('order_number', orderId);
      // forceDirect 是为了方便mock进入4.0逻辑，模拟迁移后的登录
      if (newRouter) {
        params.set('redirectToNewRc4', newRouter);
      }
      if (queryParams?.forceDirect) {
        params.set('forceDirect', queryParams?.forceDirect);
      }
      // params.set('lng', i18next.language?.split('-')[0]);
      params.set('lng', 'en');
      // 构建新的查询字符串
      const newSearch = `?${params.toString()}`;
      const envMapping = {
        prod: '',
        local: '-dev',
        dev1: '-dev',
      };
      const redirectEnv = envMapping[env] ?? `-${env}`;
      console.log(redirectEnv, newSearch);
      injectRedirectLoading(); // 展示loading动画
      // window.location.replace(`http://localhost:5173/customer-portal/17track/auto-login${newSearch}`);
      // window.location.replace(`https://resolution${redirectEnv}.seel.com/customer-portal/17track/auto-login${newSearch}`);
      window.location.replace(`https://resolve${redirectEnv}.seel.com/customer-portal/auto-login${newSearch}`);
      return;
    }
    const resp = await estimateClaim(email, orderId);
    if (!resp) {
      setIsInvalidBarShow({ show: true });
      setIsLoading(false);
      return;
    }
    const { code, data } = resp;
    if (code !== RespCode.Success) {
      // eslint-disable-next-line max-len
      const errorMap = [
        ErrorCode.Expired,
        ErrorCode.Total,
        ErrorCode.Cancelled,
        ErrorCode.HasOldClaim,
        ErrorCode.CoverdItemEmpty,
      ];
      if (errorMap.includes(code)) {
        navigate('/17track/error', { state: { code } });
      } else if (code === ErrorCode.Nothing) {
        const msg = 'No Seel covers found for this order. Please double check or contact support for help.';
        setIsInvalidBarShow({ show: true, message: msg });
      } else {
        navigate('/17track/error', { state: { code: ErrorCode.Wrong } });
      }
    } else {
      setIsInvalidBarShow({ show: false });
      const { policyList = [] } = data;
      dispatch(setCustomerInfo(data));
      // 有理赔记录直接跳转反馈页面
      if (data.claimList?.length > 0) {
        dispatch(setClaimInfo({ type: '', ...policyList[0] }));

        navigate(`/17track/feedback?claimId=${data.claimList[0]?.claimId}`);
        return;
      }
      navigate('/17track/options');
    }
    setIsLoading(false);
  } catch (error) {
    logError(error);
    setIsLoading(false);
  }
};

export const useLogin = () => {
  const {
    query: {
      email: defaultEmail = '', buyer_protection_id: defaultProtection = '', order_number: defaultOrderId = '',
    },
  } = useQuery();
  /** email */
  const [email, setEmail] = useState(defaultEmail);
  /** order id */
  const [orderId, setOrderId] = useState(defaultOrderId || defaultProtection);
  /** email 输入框状态 */
  const [emailStatus, setEmailStatus] = useState(
    defaultEmail ? InputStatus.Changed : InputStatus.Init,
  );
  /** order id 输入框状态 */
  const [orderStatus, setOrderStatus] = useState(
    (defaultOrderId || defaultProtection) ? InputStatus.Changed : InputStatus.Init,
  );
  /** 是否显示错误提示 */
  const [isInvalidBarShow, setIsInvalidBarShow] = useState({ show: false, message: '' });
  /** 按钮是否 loading */
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const setReportData = useBehaviorData();
  /** email 是否合法 */
  const isEmailValid = regEmail.test(email) || regPhoneLogin.test(email);
  /** order id 是否合法 */
  const isOrderIdValid = orderId.length > 0;
  /** 是否显示 email 非法提示 */
  const isEmailShownValid = emailStatus === InputStatus.Init || isEmailValid;
  /** 是否显示 order id 非法提示 */
  const isOrderShownValid = orderStatus === InputStatus.Init || isOrderIdValid;
  const emailLabelText = 'Email Address / Phone Number';

  /** email 非法提示文案 */
  const emailHelperText = email ? `Invalid ${emailLabelText}` : `${emailLabelText} is required`;
  /** order id 非法提示文案 */
  const orderIdHelperText = orderId ? 'Invalid Worry-Free Purchase ID / Order Number' : 'Worry-Free Purchase ID / Order Number is required';

  /** 提交按钮的状态，只有当2个都有效的情况下，才可以提交 */
  const isBtnDisable = !(isEmailValid && isOrderIdValid);

  useEffectOnce(() => {
    // 如果 url 上带有参数，自动登录
    const hasLogin = sessionStorage.getItem(SessionStorageKey.HasAutoLogin);
    if (!isBtnDisable && !hasLogin) {
      submit({
        email,
        orderId,
        setReportData,
        setIsInvalidBarShow,
        setIsLoading,
        navigate,
        dispatch,
      });
      sessionStorage.setItem(SessionStorageKey.HasAutoLogin, 'true');
    }
  });

  return {
    email,
    orderId,
    isBtnDisable,
    emailLabelText,
    emailHelperText: isEmailShownValid ? ' ' : emailHelperText,
    orderIdHelperText: isOrderShownValid ? ' ' : orderIdHelperText,
    isEmailValid: isEmailShownValid,
    isOrderIdValid: isOrderShownValid,
    isInvalidBarShow,
    isLoading,
    onChangeEmail: (event) => onChangeEmail({
      event,
      setEmail,
      emailStatus,
      setEmailStatus,
    }),
    onChangeOrderId: (event) => onChangeOrderId({
      event,
      setOrderId,
      orderStatus,
      setOrderStatus,
    }),
    onClickBtn: () => submit({
      email,
      orderId,
      setReportData,
      setIsInvalidBarShow,
      setIsLoading,
      navigate,
      dispatch,
    }),
  };
};
